import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, X } from "@phosphor-icons/react";
import { summarizeIntervals, formatIntervalValue } from "@/lib/intervals";

export const emptyIntervalRow = () => ({ min: "", sec: "", dist: "", cal: "" });

const COLS = "grid grid-cols-[1.5rem_1fr_1fr_1.3fr_1fr_1.75rem] gap-1.5 items-center";

function Cell({ value, onChange, placeholder, primary, testId, max }) {
  return (
    <Input type="number" inputMode="decimal" min="0" max={max} step="any" value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className={`h-10 px-2 rounded-lg font-data text-sm ${primary ? "border-moss ring-1 ring-moss/30" : ""}`} />
  );
}

function UnitToggle({ unit, setUnit }) {
  return (
    <div className="inline-flex bg-muted2 rounded-lg p-0.5">
      {["m", "ft", "in"].map((u) => (
        <button key={u} type="button" onClick={() => setUnit(u)}
          className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] ${unit === u ? "bg-moss text-white" : "text-ink-3"}`}
          data-testid={`interval-distance-unit-${u}`}>{u}</button>
      ))}
    </div>
  );
}

export default function IntervalResultsInput({ goal, rows, setRows, distanceUnit, setDistanceUnit, activeIndex = null }) {
  const setRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const stats = summarizeIntervals(goal, rows);
  const fmt = (v) => formatIntervalValue(goal, v, distanceUnit);
  const hdr = (key, label) => (
    <span className={`hs-label text-[9px] ${goal.log === key ? "text-moss" : ""}`}>{label}</span>
  );

  return (
    <div data-testid="interval-results">
      <div className="flex items-center justify-between gap-2">
        <Label className="hs-label">Results per interval</Label>
        <UnitToggle unit={distanceUnit} setUnit={setDistanceUnit} />
      </div>
      <div className={`${COLS} mt-2`}>
        <span />{hdr("time", "Min")}{hdr("time", "Sec")}{hdr("distance", `Dist (${distanceUnit})`)}{hdr("calories", "Cal")}<span />
      </div>
      <div className="mt-1 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} data-testid={`interval-row-${i}`}
            className={`${COLS} rounded-xl ${activeIndex === i ? "bg-sage/50 -mx-1 px-1 py-1" : ""}`}>
            <span className={`font-data text-xs ${activeIndex === i ? "text-moss font-bold" : "text-ink-3"}`}>#{i + 1}</span>
            <Cell value={r.min} onChange={(v) => setRow(i, { min: v })} placeholder="0" primary={goal.log === "time"} testId={`interval-row-${i}-min`} />
            <Cell value={r.sec} onChange={(v) => setRow(i, { sec: v })} placeholder="00" max="59" primary={goal.log === "time"} testId={`interval-row-${i}-sec`} />
            <Cell value={r.dist} onChange={(v) => setRow(i, { dist: v })} placeholder="0" primary={goal.log === "distance"} testId={`interval-row-${i}-dist`} />
            <Cell value={r.cal} onChange={(v) => setRow(i, { cal: v })} placeholder="0" primary={goal.log === "calories"} testId={`interval-row-${i}-cal`} />
            <Button type="button" size="icon" variant="ghost" disabled={rows.length <= 1}
              onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
              className="h-8 w-7 rounded-lg text-ink-3 hover:text-error" data-testid={`interval-row-${i}-remove`}>
              <X size={13} />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="secondary" onClick={() => setRows([...rows, emptyIntervalRow()])}
        className="mt-2 w-full h-9 rounded-xl text-xs" data-testid="interval-add-row">
        <Plus size={12} className="mr-1.5" /> Add interval
      </Button>
      {stats && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="interval-summary">
          {[["Total", stats.total, "total"], ["Average", stats.avg, "avg"], ["Best", stats.best, "best"]].map(([lbl, v, k]) => (
            <div key={k} className="rounded-xl bg-sage/40 border border-sage px-2.5 py-2">
              <div className="hs-label text-[9px] text-moss">{lbl}</div>
              <div className="font-data font-semibold text-sm text-moss" data-testid={`interval-summary-${k}`}>{fmt(v)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
